/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: Moment library.
import PropTypes from 'prop-types' // Npm: react.js library.
import Debounce from 'lodash/debounce' // Npm: lodash library.
import _ from 'underscore' // Npm: underscore library.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useQuery } from '@apollo/client' // Npm: Apollo client.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import DownloadToExcel from 'components/DownloadToExcel'
import Modal from 'components/Modal'
import TableSpinner from 'components/TableSpinner'
import VendorAccounttDropDownOptions from 'components/VendorAccounttDropDownOptions'
import VendorAccounttUpsert from 'components/VendorAccounttUpsert'
import VendorAccounttDelete from 'components/VendorAccounttDelete'


/*
 * GRAPHS
 */
import VendorAccounttReadQuery from './__query__/index.vendorAccountt.read.query'


/*
 * STYLES
 */
import {
  buttonStyle
} from './index.style'


/*
 * OBJECTS
 */
const Index = ({ passOn }) => {
  // Const assignment.
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 10,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({ items: [] })
  const [vendorAccounttToSearch, setVendorAccounttToSearch] = React.useState('')
  const { 'isOpen': isVendorAccounttCreateOpen, 'onOpen': onVendorAccounttCreateOpen, 'onClose': onVendorAccounttCreateClose } = useDisclosure()
  const { 'isOpen': isVendorAccounttUpdateOpen, 'onOpen': onVendorAccounttUpdateOpen, 'onClose': onVendorAccounttUpdateClose } = useDisclosure()
  const { 'isOpen': isVendorAccounttDeleteOpen, 'onOpen': onVendorAccounttDeleteOpen, 'onClose': onVendorAccounttDeleteClose } = useDisclosure()
  const _QueryVendorAccounttRead = useQuery(VendorAccounttReadQuery, {
    'variables': {
      'vendorAccounttDirectoryId': passOn?.vendorAccounttDirectoryId,
      vendorAccounttToSearch,
      'skip': paginationModel.pageSize * paginationModel.page,
      'take': paginationModel.pageSize
    },
    'fetchPolicy': Object.React.App.fetchPolicy,
    'pollInterval': Object.React.App.pollInterval
  })
  const _isFirstLoadCompleted = React.useRef(false)
  const _onVendorAccounttToSearchInputChange = React.useRef(Debounce((value) => setVendorAccounttToSearch(value), 800)).current
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({ 'base': 'false', 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })

  // Data Assignment.
  if (!_QueryVendorAccounttRead.loading && 0 < _QueryVendorAccounttRead.data?.VendorAccounttRead?.length && !_isFirstLoadCompleted.current) _isFirstLoadCompleted.current = true
  if (!_.every(_.pluck(_QueryVendorAccounttRead.data?.VendorAccounttRead, 'status'), i => _successFlags.includes(i))) _isFirstLoadCompleted.current = false

  // Extract search from filter model
  React.useEffect(() => {
    const displayNameFilter = filterModel?.items?.find(item => item.field === 'displayName')
    const searchValue = displayNameFilter?.value || ''
    _onVendorAccounttToSearchInputChange(searchValue)
  }, [filterModel])

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      _onVendorAccounttToSearchInputChange.cancel?.()
    }
  }, [])

  // Data assignment.
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S.No.',
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 180
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'headerAlign': 'center',
      'hide': true
    },
    {
      'field': 'displayName',
      'headerName': 'DisplayName',
      'description': 'Display name of vendor account.',
      'editable': false,
      'sortable': false,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'width': 300,
    },
    {
      'field': 'vendorAccountCount',
      'headerName': 'VendorAccount',
      'description': 'Number of vendor accounts.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 300,
    },
    {
      'field': 'createdAt',
      'headerName': 'Created At',
      'description': 'Creation date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 250,
    },
    {
      'field': 'updatedAt',
      'headerName': 'Updated At',
      'description': 'Last update date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 250,
    },
    {
      'field': 'action',
      'headerName': 'Actions',
      'description': 'Actions for vendor account.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 150,
      'disableColumnMenu': true,
      'renderCell': ({ row }) => <VendorAccounttDropDownOptions
        vendorAccounttId={row?.id}
        onVendorAccounttUpdate={onVendorAccounttUpdateOpen}
        onVendorAccounttDelete={onVendorAccounttDeleteOpen}
      />
    }
  ], [onVendorAccounttUpdateOpen, onVendorAccounttDeleteOpen])

  const rows = React.useMemo(() => _.compact(_QueryVendorAccounttRead?.data?.VendorAccounttRead?.map((r, index) => _successFlags.includes(r.status) ? ({
    'id': r?.id || String.random(9),
    'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
    'displayName': r?.displayName ?? '-',
    'vendorAccountCount': r?.VendorAccount?.length ?? '-',
    'createdAt': Moment(r?.createdAt).format('YYYY-MM-DD'),
    'updatedAt': Moment(r?.updatedAt).format('YYYY-MM-DD')
  }) : null)), [_QueryVendorAccounttRead?.data?.VendorAccounttRead, paginationModel.page, paginationModel.pageSize])

  // Return component.
  return (
    <>
      <Flex className='vendorAccountt base'>
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          gap={_isCurrentViewMobile ? '12px' : '22px'}
          bg='white'
          minH="81vh"
          maxH='81vh'
          borderRadius='20px'
          p={_isCurrentViewMobile ? '12px' : '22px'}>
          <Flex pb='0px' justify='space-between' gap='12px' flexWrap={{ 'base': 'wrap', 'md': 'nowrap' }}>
            <Tooltip label='Add Vendor Account' fontSize='sm'>
              <Button
                leftIcon={<IoMdAdd />}
                onClick={onVendorAccounttCreateOpen}
                style={buttonStyle}>
                Add Vendor A/C
              </Button>
            </Tooltip>
            <DownloadToExcel
              cellsData={0 === _QueryVendorAccounttRead?.data?.VendorAccounttRead?.length || !_.every(_.pluck(_QueryVendorAccounttRead?.data?.VendorAccounttRead, 'status'), i => _successFlags.includes(i)) ? [] : _QueryVendorAccounttRead?.data?.VendorAccounttRead.map((item, __index) => ({
                'S.No.': `${(__index + (paginationModel.pageSize * (paginationModel.page))) + 1}.`,
                'CreatedAt': Moment(item?.createdAt).format('YYYY-MM-DD'),
                'UpdatedAt': Moment(item?.updatedAt).format('YYYY-MM-DD'),
                'displayName': item?.displayName ?? '-',
                'VendorAccount': item.VendorAccount?.length ?? '-'
              }))}
              headersData={['S.No.', 'CreatedAt', 'UpdatedAt', 'displayName', 'VendorAccount'].map(i => ({ 'key': i, 'label': i }))} />
          </Flex>
          <DataGrid
            key={`${paginationModel.page}-${paginationModel.pageSize}`}
            rows={rows}
            columns={columns}
            rowHeight={58}
            rowCount={_.pluck(_QueryVendorAccounttRead?.data?.VendorAccounttRead, '_totalCount')?.[0] || 0}
            paginationMode='server'
            disableColumnSorting={true}
            filterModel={filterModel}
            onFilterModelChange={setFilterModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            loading={_QueryVendorAccounttRead?.loading}
          />
        </Flex>
        <Modal
          size='md'
          title='Add Vendor Account'
          isOpen={isVendorAccounttCreateOpen}
          onClose={onVendorAccounttCreateClose}>
          <VendorAccounttUpsert isCreateOnly={true} />
        </Modal>
        <Modal
          size='md'
          title='Update Vendor Account'
          isOpen={isVendorAccounttUpdateOpen}
          onClose={onVendorAccounttUpdateClose}>
          <VendorAccounttUpsert isCreateOnly={false} />
        </Modal>
        <Modal
          size='md'
          title='Remove Vendor Account'
          isOpen={isVendorAccounttDeleteOpen}
          onClose={onVendorAccounttDeleteClose}>
          <VendorAccounttDelete />
        </Modal>
      </Flex>
    </>
  )
}



/*
 * PROPTYPES
 */
Index.propTypes = {
  'passOn': PropTypes.object
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
